'use client'

import { useEffect, useState } from 'react'
import { getOtpStatus, getResendOtpInformation, sendOtpMessage, verifyOtp } from '~/actions/otp'
import { useFunnelStore } from './use-funnel-store'

export const useVerifyPhone = () => {
  const { phone } = useFunnelStore.getState().data
  const [otpStatus, setOtpStatus] = useState<'idle' | 'loading' | 'validated' | 'invalid'>('idle')
  const [resendCountdown, setResendCountdown] = useState<number | null>(0)
  const canResend = resendCountdown === 0

  const getResendInformation = async () => {
    if (!phone) return

    const response = await getResendOtpInformation(phone)
    if (!response) return setResendCountdown(0)

    const secondsMissing = Math.ceil((new Date(response.when).getTime() - Date.now()) / 1000)
    setResendCountdown(secondsMissing > 0 ? secondsMissing : 0)
  }

  const resendOtp = async () => {
    if (!phone) return
    await sendOtpMessage(phone)
    await getResendInformation()
  }

  const onOtpComplete = async (otp: string) => {
    if (!phone) return

    setOtpStatus('loading')
    setOtpStatus((await verifyOtp(phone, otp)) ? 'validated' : 'invalid')
  }

  const checkOtpStatus = async () => {
    if (phone && (await getOtpStatus(phone))) setOtpStatus('validated')
  }

  useEffect(() => {
    if (!resendCountdown) return
    const timeout = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000)
    return () => clearTimeout(timeout)
  }, [resendCountdown])

  useEffect(() => {
    if (!phone) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mount-only OTP hydrate; setState is inside checkOtpStatus after await
    void checkOtpStatus()
    void getResendInformation()
  }, [])

  return { otpStatus, canResend, resendCountdown, resendOtp, getResendInformation, onOtpComplete, checkOtpStatus }
}
